import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { PhoneValidator } from "@comunicare/comunicare-angular-forms-validator";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  FHIRHelper,
  FORMS_MODE,
  IAddPractitionerData,
  IAnsRole,
  ICoding,
  IIdentifier,
  IPractitionerInfo,
  IPractitionerRole,
  IReference,
  ISlimUserInfo,
  Practitioner,
  TELECOM_SYSTEM,
  USER_ROLE,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AnsPractitionnerResultComponent } from "src/app/components/ans-practitionner-result/ans-practitionner-result.component";
import { FormsData } from "src/app/helpers/formsData";
import { Healthcareservice } from "src/app/models/healthcareservice.model";
import { AccessGroupApiService } from "src/app/providers/api/accessGroup-api.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { OrganizationsService } from "src/app/providers/organizations.service";
import { PractitionerService } from "src/app/providers/practitioner.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { PractitionerAccessGroupsComponent } from "../access-groups-page/practitioner-access-groups/practitioner-access-groups.component";

export interface DataForUserPage {
  availableServices: IReference[];
  availableOrganizations: IReference[];
  preselectedService?: IReference;
  name?: string;
  user?: Practitioner;
  infos?: ISlimUserInfo;
}

@Component({
  selector: "app-correspondant-page",
  templateUrl: "./correspondant-page.component.html",
  styleUrls: ["./correspondant-page.component.scss"],
})
export class CorrespondantPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(PractitionerAccessGroupsComponent)
  private practitionerAccessGroupComponent: PractitionerAccessGroupsComponent;

  public mode: FORMS_MODE;
  public MODES = FORMS_MODE;
  public availableTitles: ICoding[];
  public availableQualifications: ICoding[];
  public availableRoles: ICoding[];
  public availableServices: IReference[];
  public availableOrganizations: IReference[];
  public availableLangs: ICoding[];
  public availableGender: ICoding[];
  public maxDate: moment.Moment = moment();
  public lastIndexMail: number = null;
  public lastIndexPhone: number = null;
  public isActive = false;
  public isAnsChecked = false;
  public isAnsSearchAvailable = false;
  public ansId: IIdentifier;
  public ansPractitionerRole: IPractitionerRole[];
  public ansRole: IAnsRole[];
  private ansFields = ["name", "firstname", "title", "birthDate", "inami", "nationalNumber", "userLang", "gender", "msSante"];
  public showMsSante = false;

  public practitionerForm: UntypedFormGroup = this.fb.group({
    name: ["", [Validators.required]],
    firstname: ["", [Validators.required]],
    role: ["", [Validators.required]],
    title: ["", [Validators.required]],
    birthDate: ["", [Validators.required]],
    qualif: ["", [Validators.required]],
    organisation: ["", [Validators.required]],
    services: ["", [Validators.required]],
    activationCode: ["", [Validators.required]],
    inami: ["", []],
    internalId: ["", []],
    nationalNumber: ["", []],
    needMail: ["", []],
    userLang: ["", [Validators.required]],
    gender: ["", [Validators.required]],
    login: ["", [Validators.required]],
    msSante: ["", []],
  });

  public ansForm: UntypedFormGroup = this.fb.group({
    ansGiven: ["", []],
    ansFamily: ["", []],
    msSanteSearch: ["", []],
  });

  public needMail = false;
  public user: Practitioner = null;
  public accessGroups: string[] = [];
  private data: DataForUserPage;
  private previousPage: string;
  private isInit = false;

  // All permissions of this page
  public canCreateGroup = false;
  public canReadGroups = false;
  public canReadPermissions = false;

  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();
  constructor(
    private router: Router,
    private practitionerService: PractitionerService,
    private depService: HealthcareserviceService,
    private accessGroupApiService: AccessGroupApiService,
    private fb: UntypedFormBuilder,
    private formsData: FormsData,
    private snackBar: MatSnackBar,
    private sessionService: SessionService,
    private translateService: TranslateService,
    private userService: UserService,
    private dialog: MatDialog,
    private organizationService: OrganizationsService,
    private healthcareService: HealthcareserviceService
  ) {
    this.mode = FORMS_MODE.CREATE;
    this.accessGroups = ["practitioner"];

    this.initData();

    this.checkUsersPermissions();

    // const userData: DataForUserPage = {
    //   availableServices: [],
    //   availableOrganizations: this.organizationsFiltered.map((o) => o.asReference),
    // };
    // this.router.navigate(["user"], {
    //   state: { data: userData, mode: FORMS_MODE.CREATE, previousPage: "organizations" },
    // });
  }

  ngOnInit(): void {
    if (this.mode === FORMS_MODE.UPDATE) {
      this.practitionerForm.removeControl("login");
      this.practitionerForm.removeControl("activationCode");
      this.updateForm();
      if (this.ansId) {
        this.disableAnsFields();
      }
    } else {
      this.user = this.practitionerService.createTemplate();
      this.addPhone(false, 0);
      this.addMail(false, 0);
      // bind mail with login field
      this.practitionerForm
        .get("mail0")
        .valueChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe((v: string) => {
          this.practitionerForm.get("login").setValue(v);
        });
    }
    this.isAnsSearchAvailable = this.mode === this.MODES.CREATE && this.isPreselectedAndFrOrg();

    this.translateService.onLangChange.pipe(takeUntil(this.onDestroy$)).subscribe(async () => {
      this.availableTitles = this.formsData.TITLES;
      this.availableQualifications = this.formsData.QUALIFICATIONS;
      const isAdmin = this.sessionService.isAdmin();
      this.availableRoles = this.formsData.ROLES.filter((r) => (isAdmin ? r : r.code !== USER_ROLE.ADMIN.toString()));
      this.availableGender = this.formsData.GENDER;
      this.availableLangs = await this.formsData.getLanguages();
    });
  }
  ngAfterViewInit(): void {
    this.isInit = true;
  }
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Used when we choose to add another new user, without leaving this page
   */
  private prepareForNewUser(): void {
    if (this.mode !== FORMS_MODE.CREATE) {
      return;
    }
    this.user = this.practitionerService.createTemplate();
    const previousServices = this.practitionerForm.get("services").value;
    this.practitionerForm.reset();
    this.removePhone(2);
    this.removePhone(1);
    this.removeMail(2);
    this.removeMail(1);
    if (this.data?.preselectedService) {
      this.practitionerForm.get("services").setValue(previousServices);
      this.practitionerForm.get("organisation").setValue(this.availableOrganizations[0]);
    }
    this.practitionerForm.markAsPristine();
  }

  private async initData(): Promise<void> {
    const isAdmin = this.sessionService.isAdmin();
    this.availableTitles = this.formsData.TITLES;
    this.availableQualifications = this.formsData.QUALIFICATIONS;
    this.availableRoles = this.formsData.ROLES.filter((r) => (isAdmin ? r : r.code !== USER_ROLE.ADMIN.toString()));
    this.accessGroups = [];
    this.availableGender = this.formsData.GENDER;
    this.user = this.data?.user ? new Practitioner(this.data?.user) : undefined;
    this.availableServices = this.healthcareService.availableServices().map((s) => s.asReference); // !!
    this.availableOrganizations = this.userService.allOrganizations.map((o) => o.asReference); // !!
    if (this.data?.preselectedService) {
      const preServicesRefs = this.availableServices.filter((s) => s.reference === this.data?.preselectedService?.reference);
      this.practitionerForm.get("services").setValue(preServicesRefs);
      this.practitionerForm.get("organisation").setValue(this.availableOrganizations[0]);
      const defaultGroups = this.getDefaultAccessGroups(preServicesRefs);
      const groups = [...this.accessGroups, ...defaultGroups];
      this.accessGroups = Tools.uniq(groups);
    }
    this.practitionerForm
      .get("organisation")
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((v: IReference) => {
        if (!this.isInit) {
          return;
        }
        this.depService
          .list(v.reference)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((services: Healthcareservice[]) => {
            this.availableServices = services.map((s) => s.asReference);
            if (this.mode === FORMS_MODE.UPDATE) {
              const services = this.availableServices.filter((s) => this.user.allServicesRefs.indexOf(s.reference) !== -1);
              this.practitionerForm.controls.services.setValue(services);
            }
          });
      });
    this.practitionerForm
      .get("services")
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((v: IReference[]) => {
        if (this.isInit) {
          const prevValue = this.practitionerForm.value["services"];
          const newServicesRefs = v.filter((s) => !prevValue.includes(s));
          const defaultGroups = this.getDefaultAccessGroups(newServicesRefs);
          const groups = [...this.accessGroups, ...defaultGroups];
          this.accessGroups = Tools.uniq(groups);
        }
      });
    this.availableLangs = await this.formsData.getLanguages();
  }

  private updateForm(): void {
    if (this.mode === FORMS_MODE.CREATE) {
      return;
    }
    const orga = this.availableOrganizations.find((or) => or.reference === this.user.organization?.reference);
    const services = this.availableServices.filter((s) => this.user.allServicesRefs.indexOf(s.reference) !== -1);
    const gender = this.availableGender.find((g) => g.code === this.user.gender)?.code;
    const role = this.availableRoles.find((r) => r.code === this.data?.infos.role?.[0]?.toString())?.code;
    const lang = this.availableLangs.find((l) => l.code === this.user.mainLang)?.code;
    this.isActive = this.user.active;
    this.practitionerForm.controls.name.setValue(this.user.familyName);
    this.practitionerForm.controls.firstname.setValue(this.user.firstname);
    this.practitionerForm.controls.role.setValue(role);
    this.practitionerForm.controls.title.setValue(this.transformPrefixToValidi18nKey(this.user.prefix));
    this.practitionerForm.controls.birthDate.setValue(this.user.birthDate);
    this.practitionerForm.controls.qualif.setValue(this.user.mainQualif?.code);
    this.practitionerForm.controls.organisation.setValue(orga);
    this.practitionerForm.controls.services.setValue(services);
    this.practitionerForm.controls.inami.setValue(this.user.inamiNbr);
    this.practitionerForm.controls.nationalNumber.setValue(this.data?.infos.nationalnumber);
    this.practitionerForm.controls.userLang.setValue(lang);
    this.practitionerForm.controls.gender.setValue(gender);
    this.practitionerForm.controls.msSante.setValue(this.getMsSante());
    if (this.user.phoneNbr.length) {
      this.user.phoneNbr.forEach((v, index) => {
        this.addPhoneControl(v, index);
      });
    } else {
      this.addPhone(false, 0);
    }

    if (this.user.mailAdress.length) {
      this.user.mailAdress.forEach((v, index) => {
        this.addMailControl(v, index);
      });
    } else {
      this.addMail(false, 0);
    }
    this.ansId = FHIRHelper.getAnsId(this.data?.user?.identifier);
    this.ansPractitionerRole = this.data?.user?.practitionerRole.slice(1);
    this.ansRole = this.data?.user?.ansRole;

    this.practitionerForm.markAsPristine();
    this.practitionerForm.markAsUntouched();
  }

  /**
   * Add the default access groups' ids from the selected services to the users.
   * If there are services refs in the parameters, it will use those rather than the selected ones.
   * If there are no default access groups in the service, it will take the default access groups from the
   * organization.
   * @param servicesRefs (optional) the services from which we want the default access groups' ids
   * @returns
   */
  private getDefaultAccessGroups(servicesRefs?: IReference[]): string[] {
    this.practitionerAccessGroupComponent?.save();
    const selectedServicesRefs: string[] = servicesRefs
      ? servicesRefs.map((s) => s.reference)
      : this.practitionerForm.get("services").value.map((s) => s.reference);
    const selectedServices = this.userService.allServices.filter((s) => selectedServicesRefs.includes(s.asReference.reference));
    const defaultAccessGroups: string[] = [];
    for (const service of selectedServices) {
      if (service.defaultAccessGroups?.length) {
        defaultAccessGroups.push(...service.defaultAccessGroups);
      } else {
        const org = this.userService.allOrganizations.find((o) => o.asReference.reference === service.organizationRef);
        if (org && org.defaultAccessGroups) {
          defaultAccessGroups.push(...org.defaultAccessGroups);
        }
      }
    }
    return Tools.uniq(defaultAccessGroups);
  }

  public async apply(addAnotherUser = false): Promise<void> {
    if (!this.practitionerForm.valid) {
      this.practitionerForm.markAsTouched();
      this.practitionerForm.markAsDirty();
      return;
    }
    this.practitionerAccessGroupComponent.save();

    const practitionerFormRawValue = this.practitionerForm.getRawValue();
    // Resolve qualif code back to full object
    const qualifCode = practitionerFormRawValue.qualif;
    const qualif = this.availableQualifications.find((q) => q.code === qualifCode);
    // Resolve title code back to translated term
    const titleCode = practitionerFormRawValue.title;
    const title = this.availableTitles.find((q) => q.code === titleCode).display;

    const result: IAddPractitionerData = {
      ...practitionerFormRawValue,
      qualif: qualif, // override
      title: title, // override
      needMail: this.needMail,
      user: this.user,
      active: this.isActive,
      accessGroups: this.accessGroups,
      ansId: this.ansId,
      ansPractitionerRole: this.ansPractitionerRole,
      ansRole: this.ansRole,
    };
    if (this.mode === FORMS_MODE.UPDATE) {
      this.practitionerService.update(result).subscribe(
        () => {
          this.translateService.get("common.saveSuccess").subscribe((trans) => {
            this.snackBar.open(trans, "ok", { duration: 10000 });
          });
        },
        (err) => {
          FileLogger.error("CorrespondantPageComponent", "Error while updating user", err);
          this.translateService.get("common.saveFail").subscribe((trans) => {
            this.snackBar.open(trans, "ok", { duration: 10000 });
          });
        },
        () => {
          this.router.navigate([this.previousPage]);
        }
      );
    } else if (this.mode === FORMS_MODE.CREATE) {
      this.practitionerService.create(result).subscribe(
        () => {
          this.translateService.get("common.saveSuccess").subscribe((trans) => {
            this.snackBar.open(trans, "ok", { duration: 10000 });
          });
        },
        (err) => {
          if (err.message === "ALREADY EXISTS") {
            const alreadyExistsi18n = "dialog.practitionerAlreadyExists.databaseExistingMailPractitioner";
            this.snackBar.open(this.translateService.instant(alreadyExistsi18n), "ok", { duration: 10000 });
          } else if (err.message === "DUPLICATE WARNING") {
            const message = this.translateService.instant("dialog.correspondentAlreadyExists.duplicateNameWarning");
            this.dialog
              .open(ConfirmationDialogComponent, {
                data: { message, type: ConfirmationDialogType.CONFIRM },
              })
              .afterClosed()
              .subscribe((confirmed) => {
                if (confirmed) {
                  this.practitionerService.create(result, true).subscribe(
                    () => {
                      this.translateService.get("common.saveSuccess").subscribe((trans) => {
                        this.snackBar.open(trans, "ok", { duration: 10000 });
                      });
                    },
                    () => {
                      this.snackBar.open(this.translateService.instant("common.saveFail"), "ok", { duration: 10000 });
                    },
                    () => {
                      if (!addAnotherUser) {
                        this.router.navigate(["/"]);
                      } else {
                        this.prepareForNewUser();
                      }
                    }
                  );
                }
              });
          } else {
            this.snackBar.open(this.translateService.instant("common.saveFail"), "ok", { duration: 10000 });
          }
        },
        () => {
          if (!addAnotherUser) {
            this.router.navigate(["/"]);
          } else {
            this.prepareForNewUser();
          }
        }
      );
    }
  }

  private addPhoneControl(v: IIdentifier, index: number): void {
    this.practitionerForm.addControl(
      `phone${index}`,
      this.fb.control(v.value, index === 0 ? [Validators.required, this.phoneValidator] : this.phoneValidator)
    );
    this.practitionerForm.addControl(`phoneComment${index}`, this.fb.control(v.label));
  }

  private addMailControl(v: IIdentifier, index: number): void {
    this.practitionerForm.addControl(
      `mail${index}`,
      this.fb.control(v.value, index === 0 ? [Validators.required, Validators.email] : Validators.email)
    );
    this.practitionerForm.addControl(`mailComment${index}`, this.fb.control(v.label));
  }

  public addPhone(fromTemplate: boolean, index?: number): void {
    index = fromTemplate ? this.user.phoneNbr.length : index;
    this.addPhoneControl(this.getTemplate(TELECOM_SYSTEM.PHONE), index);
    this.user.addTelecom(this.getTemplate(TELECOM_SYSTEM.PHONE));
  }

  public addMail(fromTemplate: boolean, index?: number): void {
    index = fromTemplate ? this.user.mailAdress.length : index;
    this.addMailControl(this.getTemplate(TELECOM_SYSTEM.MAIL), index);
    this.user.addTelecom(this.getTemplate(TELECOM_SYSTEM.MAIL));
  }

  public removeMail(index: number): void {
    if (!this.practitionerForm.contains(`mail${index}`)) {
      return;
    }
    this.user.removeTelecom(TELECOM_SYSTEM.MAIL);
    this.practitionerForm.removeControl(`mail${index}`);
    this.practitionerForm.removeControl(`mailComment${index}`);
  }

  public removePhone(index: number): void {
    if (!this.practitionerForm.contains(`phone${index}`)) {
      return;
    }
    this.user.removeTelecom(TELECOM_SYSTEM.PHONE);
    this.practitionerForm.removeControl(`phone${index}`);
    this.practitionerForm.removeControl(`phoneComment${index}`);
  }

  private getTemplate(type: TELECOM_SYSTEM): IIdentifier {
    return {
      value: null,
      use: null,
      label: null,
      system: type,
    };
  }

  public phoneValidator(control: AbstractControl): { [key: string]: true } | null {
    return PhoneValidator.phoneValidator(control);
  }

  public generatePassword(): void {
    this.practitionerForm.get("activationCode").setValue(Tools.generatePassword(8));
  }

  private transformPrefixToValidi18nKey(prefix: string): string {
    if (prefix === "Dr." || prefix === "DR." || prefix === "Dott.") {
      return "doctor.short";
    } else if (prefix === "Mme" || prefix === "Mrs." || prefix === "Frau" || prefix === "Mw" || prefix === "Sig.ra" || prefix === "Sra.") {
      return "miss.short";
    } else if (prefix === "M." || prefix === "Herr." || prefix === "Sig." || prefix === "Sr.") {
      return "mister.short";
    } else if (prefix === "Pr" || prefix === "Pr." || prefix === "Prof.") {
      return "professor.short";
    } else {
      return "";
    }
  }

  private checkUsersPermissions(): void {
    this.canReadGroups = this.userService.isAuthorizedSync(null, this.accessGroupApiService.readRoutes[0], "GET");
    this.canReadPermissions = this.userService.isAuthorizedSync(null, this.accessGroupApiService.readRoutes[1], "GET");
    this.canCreateGroup = this.userService.isAuthorizedSync(null, this.accessGroupApiService.createRoutes[0], "POST");
  }

  public onSearchAns(isMsSante = false): void {
    const dialogRef = this.dialog.open(AnsPractitionnerResultComponent, {
      data: {
        given: this.ansForm.get("ansGiven").value,
        family: this.ansForm.get("ansFamily").value,
        msSante: isMsSante ? this.ansForm.get("msSanteSearch").value : undefined,
      },
    });
    dialogRef.afterClosed().subscribe((result: IPractitionerInfo) => {
      if (result) {
        this.setAnsValue(result);
      }
    });
  }

  private setAnsValue(p: IPractitionerInfo) {
    this.ansFields.forEach((f) => {
      const fieldForm = this.practitionerForm.get(f);
      if (f === "inami") {
        f = "practitionerReferenceNumber";
      }
      if (f === "userLang") {
        f = "language";
      }

      if (f === "msSante" && p?.telecom?.value) {
        this.showMsSante = true;
        fieldForm.setValue(p.telecom.value);
        fieldForm.disable();
      }

      if (Tools.isDefined(p[f])) {
        fieldForm.setValue(p[f]);
        fieldForm.disable();
      }
    });
    this.ansId = p.identifier;
    if (p.practitionerRole) {
      this.ansPractitionerRole = p.practitionerRole;
    }
    if (p.ansRole) {
      this.ansRole = p.ansRole;
    }
  }
  private isPreselectedAndFrOrg() {
    return true;
  }

  private disableAnsFields() {
    this.ansFields.forEach((f) => {
      const fieldForm = this.practitionerForm.get(f);
      fieldForm.disable();
    });
  }

  private getMsSante(): string {
    const msSante = this.data?.user.telecom.find((t) => {
      return t.system === TELECOM_SYSTEM.MSSANTE;
    });
    if (msSante?.value) {
      this.showMsSante = true;
      return msSante.value;
    }
    return undefined;
  }
}
